import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError } from '@/api/client'
import { Badge, Button, Card, Skeleton } from '@/ui/primitives'
import { Camp } from '@/ui/form'
import { Bifa, BaraStare, CampParola, FaraDrept, Grup } from './comune'

type Setari = {
  sagaweb_enabled?: boolean; sagaweb_email?: string; sagaweb_password_masked?: string
  sagaweb_company_cui?: string; sagaweb_company_name?: string
  sagaweb_auto_export?: boolean; sagaweb_auto_import?: boolean
  saga_enabled?: boolean; saga_api_url?: string; saga_username?: string; saga_password_masked?: string
}
type Stare = {
  enabled: boolean; connection_type: string; email: string; company_name: string; company_cui: string
  last_export: string | null; last_export_status: string | null
  last_import: string | null; last_import_status: string | null
}

const clipa = (s: string | null) =>
  s ? new Date(s).toLocaleString('ro-RO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'

export function SetariSaga() {
  const qc = useQueryClient()
  const [f, setF] = useState<Setari & { parolaWeb: string; parolaApi: string }>({ parolaWeb: '', parolaApi: '' })
  const [salvat, setSalvat] = useState(false)
  const [eroare, setEroare] = useState<string | null>(null)
  const [test, setTest] = useState<string | null>(null)

  const setari = useQuery({
    queryKey: ['saga-setari'], retry: false,
    queryFn: () => api.get<{ settings: Setari }>('/saga/settings'),
  })
  const stare = useQuery({
    queryKey: ['saga-stare'], retry: false,
    queryFn: () => api.get<Stare>('/saga/status'),
  })

  useEffect(() => {
    if (setari.data?.settings) setF((x) => ({ ...setari.data.settings, parolaWeb: x.parolaWeb, parolaApi: x.parolaApi }))
  }, [setari.data])

  const faraDrept = [setari.error, stare.error].some((e) => (e as ApiError | null)?.status === 403)

  const salveaza = useMutation({
    mutationFn: () => api.post('/saga/settings', {
      sagaweb_enabled: Boolean(f.sagaweb_enabled),
      sagaweb_email: f.sagaweb_email ?? '',
      sagaweb_password: f.parolaWeb,
      sagaweb_company_cui: f.sagaweb_company_cui ?? '',
      sagaweb_company_name: f.sagaweb_company_name ?? '',
      sagaweb_auto_export: Boolean(f.sagaweb_auto_export),
      sagaweb_auto_import: Boolean(f.sagaweb_auto_import),
      saga_enabled: Boolean(f.saga_enabled),
      saga_api_url: f.saga_api_url ?? '',
      saga_username: f.saga_username ?? '',
      saga_password: f.parolaApi,
    }),
    onSuccess: () => {
      setSalvat(true); setF((x) => ({ ...x, parolaWeb: '', parolaApi: '' }))
      qc.invalidateQueries({ queryKey: ['saga-setari'] })
      qc.invalidateQueries({ queryKey: ['saga-stare'] })
    },
    onError: (e) => setEroare(e instanceof Error ? e.message : 'Nu s-a putut salva'),
  })

  /** Testul doar se autentifica si citeste — exporturile si importurile raman acolo unde
   *  le porneste omul sau cronul, ca sa nu plece nimic spre contabilitate dintr-un ecran
   *  de configurare. */
  const testeaza = useMutation({
    mutationFn: () => api.post<{ success: boolean; message?: string; detail?: string }>('/saga/test-connection'),
    onSuccess: (r) => setTest(r.success ? 'Conexiune reusita' : (r.message ?? r.detail ?? 'Conexiune esuata')),
    onError: (e) => setTest(e instanceof Error ? e.message : 'Conexiune esuata'),
  })

  if (faraDrept) return <div className="px-8 py-6"><FaraDrept ce="Configurarea SAGA" /></div>
  if (setari.isPending) return <div className="px-8 py-6"><Card><Skeleton rows={8} /></Card></div>

  const s = stare.data

  return (
    <div className="px-8 py-6 space-y-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <Card className="p-4">
          <p className="text-[12.5px] text-muted">Legatura</p>
          <p className="mt-1"><Badge tone={s?.enabled ? 'good' : 'neutral'}>{s?.enabled ? 'pornita' : 'oprita'}</Badge></p>
          <p className="mt-1 text-[12px] text-muted">{s?.connection_type}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[12.5px] text-muted">Ultimul export</p>
          <p className="mt-1 text-[15px]">{clipa(s?.last_export ?? null)}</p>
          {s?.last_export_status && <p className="mt-1 text-[12px] text-muted">{s.last_export_status}</p>}
        </Card>
        <Card className="p-4">
          <p className="text-[12.5px] text-muted">Ultimul import</p>
          <p className="mt-1 text-[15px]">{clipa(s?.last_import ?? null)}</p>
          {s?.last_import_status && <p className="mt-1 text-[12px] text-muted">{s.last_import_status}</p>}
        </Card>
      </div>

      <Grup titlu="SagaWeb (citire si depunere facturi)">
        <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
          <Camp eticheta="Email" id="sw-email" tip="email" valoare={f.sagaweb_email ?? ''}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, sagaweb_email: v })) }} />
          <CampParola eticheta="Parola" id="sw-pass" valoare={f.parolaWeb} masca={f.sagaweb_password_masked}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, parolaWeb: v })) }} />
          <Camp eticheta="Denumire firma" id="sw-nume" valoare={f.sagaweb_company_name ?? ''}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, sagaweb_company_name: v })) }} />
          <Camp eticheta="CUI" id="sw-cui" valoare={f.sagaweb_company_cui ?? ''}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, sagaweb_company_cui: v })) }} />
        </div>
        <div className="mt-3 space-y-2">
          <Bifa eticheta="Legatura cu SagaWeb pornita" valoare={Boolean(f.sagaweb_enabled)}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, sagaweb_enabled: v })) }} />
          <Bifa eticheta="Depune automat facturile in coada SAGA" valoare={Boolean(f.sagaweb_auto_export)}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, sagaweb_auto_export: v })) }}
            indiciu="Facturile ajung in coada; importul in SAGA il apasa contabilul" />
          <Bifa eticheta="Citeste automat platile din SAGA" valoare={Boolean(f.sagaweb_auto_import)}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, sagaweb_auto_import: v })) }} />
        </div>
      </Grup>

      <Grup titlu="SAGA REST (cheie de acces)">
        <div className="grid gap-4 sm:grid-cols-2 max-w-2xl">
          <Camp eticheta="Adresa API" id="sa-url" valoare={f.saga_api_url ?? ''}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, saga_api_url: v })) }} latime="plin" />
          <Camp eticheta="Utilizator" id="sa-user" valoare={f.saga_username ?? ''}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, saga_username: v })) }} />
          <CampParola eticheta="Cheie / parola" id="sa-pass" valoare={f.parolaApi} masca={f.saga_password_masked}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, parolaApi: v })) }} />
        </div>
        <div className="mt-3">
          <Bifa eticheta="Legatura REST pornita" valoare={Boolean(f.saga_enabled)}
            seteaza={(v) => { setSalvat(false); setF((x) => ({ ...x, saga_enabled: v })) }}
            indiciu={'Cand cheia expira, depunerile pica cu „Cheie de acces invalida" — se regenereaza din SAGA Web'} />
        </div>
        <div className="mt-4">
          <BaraStare salveaza={() => { setEroare(null); salveaza.mutate() }} seSalveaza={salveaza.isPending}
            salvat={salvat} eroare={eroare}
            copii={
              <>
                <Button onClick={() => { setTest(null); testeaza.mutate() }} disabled={testeaza.isPending}>
                  {testeaza.isPending ? 'Se verifica…' : 'Verifica legatura'}
                </Button>
                {test && <span className="text-[13px] text-muted">{test}</span>}
              </>
            } />
        </div>
      </Grup>
    </div>
  )
}
